import { Alert, Button, Card, Group, Loader, SimpleGrid, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconDownload, IconFileSpreadsheet } from '@tabler/icons-react';
import * as XLSX from 'xlsx';

import { EtfPcfData } from '../../types';
import { isEtfSectionError, useEtfContext } from './etf_common';

//every field quoted, CRLF + BOM so Excel opens it cleanly (same as generate_services_excel_modal)
const downloadCsv = (filename: string, header: string[], rows: (string | number | null)[][]) => {
    const cell = (v: string | number | null) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

//in-kind basket the AP delivers: constituents + the estimated cash line
const downloadBasketCsv = (pcf: EtfPcfData) => {
    const header = ['Security', 'Ticker', 'Name', 'ISIN', 'SEDOL', 'Latest Price (Local)', 'Quantity', 'Bonus Shares Pending', 'Market Value (Local)', 'Currency', 'FX Rate', `Market Value (${pcf.baseCurrency})`, 'Weight %'];
    const rows = pcf.constituents.map((c) => [
        c.securityCode, c.alternativeTicker, c.securityDescription, c.isin, c.sedol, c.marketPriceLocal, c.basketQuantity, c.bonusSharesPending,
        c.marketValueLocal, c.tradingCurrency, c.fxRate, c.marketValueBase, (c.benchmarkWeightBase * 100).toFixed(4),
    ]);
    rows.push(['CASH', '', 'Cash per creation unit', '', '', '', '', '', '', pcf.baseCurrency, 1, pcf.cashPerCreationUnit, '']);
    rows.push(['DIVIDEND DUE', '', 'Cash dividends declared, not credited yet, per creation unit', '', '', '', '', '', '', pcf.baseCurrency, 1, pcf.dividendDuePerCreationUnit, '']);
    rows.push(['TOTAL', '', `${pcf.sharesPerCreationUnit.toLocaleString()} ETF units per creation unit`, '', '', '', '', '', '', pcf.baseCurrency, '', pcf.basketMarketValueBase + pcf.estimatedCashPerCreationUnit, '100']);
    downloadCsv(`etf_basket_${pcf.fund}_${pcf.positionAsOf ?? 'latest'}.csv`, header, rows);
};

//exact GTN PCF layout (docs/GTN_Example_PCF.xltx): same 26 column names A-Z, header fields repeated on every
//constituent row, sheet named PCF_<ticker>_<yyyymmdd>. This is what the calculation agent uses for the iNAV
//(Estimated Cash Per Creation Unit is part of it).
const PCF_COLUMNS = [
    'Basket Name', 'ETF Issuer Name', 'ETF Trading ID', 'ETF Trading Ticker', 'Basket Evaluation Date',
    'ETF Shares Per Creation Unit', 'Total Number of Included Constituents', 'ETF Base Currency', 'Official NAV',
    'ETF Shares Outstanding', 'Security Description', 'ISIN', 'SEDOL', 'CUSIP', 'Basket Quantity', 'Trading Currency',
    'Clean Market Price (Local)', 'Market Price (Local)', 'Market Value or Unrealized (Local)', 'FX Rate',
    'Market Value or Unrealized (Base)', 'Benchmark Weight (Base)', 'Base Dividend Rate', 'Estimated Cash Per Creation Unit',
    'Alternative Ticker', 'Basket Client Disclaimer',
];

export const pcfRows = (pcf: EtfPcfData): (string | number | Date | null)[][] => {
    const evaluationDate = new Date(`${pcf.evaluationDate}T00:00:00`);
    return pcf.constituents.map((c) => [
        pcf.basketName, pcf.issuerName, pcf.tradingId, pcf.tradingTicker, evaluationDate,
        pcf.sharesPerCreationUnit, pcf.totalConstituents, pcf.baseCurrency, pcf.officialNav,
        pcf.sharesOutstanding, c.securityDescription, c.isin, c.sedol, c.cusip, c.basketQuantity, c.tradingCurrency,
        c.cleanMarketPriceLocal, c.marketPriceLocal, c.marketValueLocal, c.fxRate,
        c.marketValueBase, c.benchmarkWeightBase, c.baseDividendRate, pcf.estimatedCashPerCreationUnit,
        c.alternativeTicker, pcf.disclaimer,
    ]);
};

const downloadPcfXlsx = (pcf: EtfPcfData) => {
    const ws = XLSX.utils.aoa_to_sheet([PCF_COLUMNS, ...pcfRows(pcf)], { cellDates: true });
    pcf.constituents.forEach((_, i) => { ws[XLSX.utils.encode_cell({ r: i + 1, c: 4 })].z = 'm/d/yyyy'; }); //column E like the template
    const wb = XLSX.utils.book_new();
    const stamp = pcf.evaluationDate.replace(/-/g, '');
    XLSX.utils.book_append_sheet(wb, ws, `PCF_${pcf.tradingTicker || pcf.fund}_${stamp}`);
    XLSX.writeFile(wb, `PCF_${pcf.tradingTicker || pcf.fund}_${stamp}.xlsx`);
};

const EtfPcf = () => {
    const { data, loading } = useEtfContext();

    if (loading && !data) return <Loader />;
    if (!data) return <Text c="dimmed">Waiting for the synced position — click <b>Refresh Data</b> if none has been pulled yet.</Text>;
    if (isEtfSectionError(data.pcf)) {
        return (
            <Alert color="red" icon={<IconAlertTriangle />} title="PCF unavailable">
                {data.pcf.error}
            </Alert>
        );
    }

    const pcf = data.pcf;
    const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
    const missingIds = pcf.constituents.filter((c) => !c.isin || !c.sedol).map((c) => c.alternativeTicker);

    const header: { label: string; value: string }[] = [
        { label: 'Basket Name', value: pcf.basketName || '—' },
        { label: 'ETF Issuer Name', value: pcf.issuerName || '—' },
        { label: 'ETF Trading ID', value: pcf.tradingId || '—' },
        { label: 'ETF Trading Ticker', value: pcf.tradingTicker || '—' },
        { label: 'Basket Evaluation Date', value: pcf.evaluationDate },
        { label: 'Position As Of', value: pcf.positionAsOf ?? '—' },
        { label: 'Official NAV', value: `${pcf.officialNav} (${pcf.navDate ?? 'no date'})` },
        { label: 'ETF Shares Outstanding', value: pcf.sharesOutstanding.toLocaleString() },
        { label: 'ETF Shares Per Creation Unit', value: pcf.sharesPerCreationUnit.toLocaleString() },
        { label: 'Basket Ratio', value: (pcf.sharesPerCreationUnit / pcf.sharesOutstanding).toFixed(6) },
        { label: 'Total Constituents', value: String(pcf.totalConstituents) },
        { label: `Basket Market Value (${pcf.baseCurrency})`, value: fmt(pcf.basketMarketValueBase) },
        { label: 'Cash Per Creation Unit', value: fmt(pcf.cashPerCreationUnit) },
        { label: 'Dividend Due Per Creation Unit', value: fmt(pcf.dividendDuePerCreationUnit) },
        { label: 'Estimated Cash Per Creation Unit', value: fmt(pcf.estimatedCashPerCreationUnit) },
    ];

    return (
        <>
            {pcf.stale && (
                <Alert color="red" variant="filled" icon={<IconAlertTriangle />} title="NAV IS STALE" mb="md">
                    The PCF is built on a NAV dated {pcf.navDate ?? 'unknown'} — sync the NAV first.
                </Alert>
            )}
            {missingIds.length > 0 && (
                <Alert color="yellow" icon={<IconAlertTriangle />} title="Missing ISIN / SEDOL" mb="md">
                    No identifiers for {missingIds.join(', ')} — fill them in on the Identifiers tab (or on the securities doc).
                </Alert>
            )}
            <Group justify="space-between" mb="md">
                <Text size="sm" c="dimmed">
                    Basket quantities are floored whole shares (fund quantity × {pcf.sharesPerCreationUnit.toLocaleString()} / {pcf.sharesOutstanding.toLocaleString()}); weights are over the basket's securities value.
                </Text>
                <Group>
                    <Button variant="light" leftSection={<IconDownload size={16} />} onClick={() => downloadBasketCsv(pcf)}>
                        Download Basket (with cash)
                    </Button>
                    <Button leftSection={<IconFileSpreadsheet size={16} />} onClick={() => downloadPcfXlsx(pcf)}>
                        Download PCF (GTN Excel)
                    </Button>
                </Group>
            </Group>
            <SimpleGrid cols={{ base: 2, md: 4, lg: 7 }} mb="md">
                {header.map((item) => (
                    <Card key={item.label} withBorder padding="sm">
                        <Text size="xs" c="dimmed">{item.label}</Text>
                        <Text fw={600}>{item.value}</Text>
                    </Card>
                ))}
            </SimpleGrid>
            <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Ticker</Table.Th>
                        <Table.Th>Security Description</Table.Th>
                        <Table.Th>ISIN</Table.Th>
                        <Table.Th>SEDOL</Table.Th>
                        <Table.Th ta="right">Basket Quantity</Table.Th>
                        <Table.Th ta="right">Bonus Pending</Table.Th>
                        <Table.Th>Currency</Table.Th>
                        <Table.Th ta="right">Price (Local)</Table.Th>
                        <Table.Th ta="right">Market Value (Local)</Table.Th>
                        <Table.Th ta="right">FX Rate</Table.Th>
                        <Table.Th ta="right">Market Value ({pcf.baseCurrency})</Table.Th>
                        <Table.Th ta="right">Weight</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {pcf.constituents.map((c) => (
                        <Table.Tr key={c.securityCode}>
                            <Table.Td>{c.alternativeTicker}</Table.Td>
                            <Table.Td>{c.securityDescription}</Table.Td>
                            <Table.Td>{c.isin || '—'}</Table.Td>
                            <Table.Td>{c.sedol || '—'}</Table.Td>
                            <Table.Td ta="right">{c.basketQuantity.toLocaleString()}</Table.Td>
                            <Table.Td ta="right">{c.bonusSharesPending ? fmt(c.bonusSharesPending) : '—'}</Table.Td>
                            <Table.Td>{c.tradingCurrency}</Table.Td>
                            <Table.Td ta="right">{c.marketPriceLocal.toFixed(3)}</Table.Td>
                            <Table.Td ta="right">{fmt(c.marketValueLocal)}</Table.Td>
                            <Table.Td ta="right">{c.fxRate}</Table.Td>
                            <Table.Td ta="right">{fmt(c.marketValueBase)}</Table.Td>
                            <Table.Td ta="right">{(c.benchmarkWeightBase * 100).toFixed(2)}%</Table.Td>
                        </Table.Tr>
                    ))}
                    <Table.Tr>
                        <Table.Td>CASH</Table.Td>
                        <Table.Td colSpan={5}>Cash per creation unit (position cash × ratio)</Table.Td>
                        <Table.Td>{pcf.baseCurrency}</Table.Td>
                        <Table.Td colSpan={3} />
                        <Table.Td ta="right">{fmt(pcf.cashPerCreationUnit)}</Table.Td>
                        <Table.Td />
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Td>DIVIDEND DUE</Table.Td>
                        <Table.Td colSpan={5}>Cash dividends declared, not credited yet (× ratio)</Table.Td>
                        <Table.Td>{pcf.baseCurrency}</Table.Td>
                        <Table.Td colSpan={3} />
                        <Table.Td ta="right">{fmt(pcf.dividendDuePerCreationUnit)}</Table.Td>
                        <Table.Td />
                    </Table.Tr>
                </Table.Tbody>
                <Table.Tfoot>
                    <Table.Tr fw={700}>
                        <Table.Td colSpan={4}>Total ({pcf.totalConstituents} constituents + cash)</Table.Td>
                        <Table.Td colSpan={6} />
                        <Table.Td ta="right">{fmt(pcf.basketMarketValueBase + pcf.estimatedCashPerCreationUnit)}</Table.Td>
                        <Table.Td ta="right">100.00%</Table.Td>
                    </Table.Tr>
                </Table.Tfoot>
            </Table>
            {pcf.disclaimer && <Text size="xs" c="dimmed" mt="md">{pcf.disclaimer}</Text>}
        </>
    );
};

export default EtfPcf;
